import { useMemo, useState } from "react";
import useSWR from "swr";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import type {
  CashflowCategory,
  CashflowSummary,
  ConsolidatedCashflowResponse,
  Currency,
} from "../../types";
import { cfApi, cfFetch } from "../../api/apiClient";
import { formatCurrency } from "../cashflow/CashflowContext";
import { useCategories } from "@/hooks/useCategories";
import {
  drillCategoryIdForExpenseSlice,
  mergeExpenseCategoriesByNormalizedName,
  rollupExpenseCategoriesToParentsOnly,
} from "./dashboardUtils";
import { PieChartEmptyContent } from "../datamintEmptyStates";

type RangeMode = "rolling30" | "full";

function formatCenterTotal(amount: number, currency: Currency): string {
  const symbol = currency === "ILS" ? "₪" : "$";
  if (Math.abs(amount) >= 1_000_000) {
    return `${symbol}${(amount / 1_000_000).toFixed(0)}M`;
  }
  if (Math.abs(amount) >= 1000) {
    return `${symbol}${(amount / 1000).toFixed(0)}K`;
  }
  return formatCurrency(amount, currency);
}

/** Inclusive rolling window: today and the prior 29 calendar days (30 days total). */
function last30DaysRange(): { dateFrom: string; dateTo: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return {
    dateFrom: start.toISOString().split("T")[0],
    dateTo: end.toISOString().split("T")[0],
  };
}

async function fetchCashflowSummaryForBreakdown(
  url: string,
): Promise<CashflowSummary> {
  const res = await cfFetch(url);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || res.statusText || String(res.status));
  }
  const data = (await res.json()) as
    | ConsolidatedCashflowResponse
    | CashflowSummary;
  if (data && typeof data === "object" && "meta" in data && data.meta) {
    const { meta: _m, ...rest } = data as ConsolidatedCashflowResponse;
    return rest;
  }
  return data as CashflowSummary;
}

type ExpenseRow = {
  name: string;
  color: string;
  value: number;
  /** Representative category id (merged slices may combine several ids) */
  categoryId: string;
  /** Stable React key (composite when duplicate labels were merged) */
  rowKey: string;
};

function expenseSources(c: CashflowCategory): CashflowCategory[] {
  return c.mergeSources?.length ? c.mergeSources : [c];
}

function buildExpenseRows(summary: CashflowSummary | undefined): ExpenseRow[] {
  if (!summary?.expenseCategories?.length) return [];
  const cats = summary.expenseCategories;

  function mapCategory(c: CashflowCategory, sliceValue: number): ExpenseRow {
    const sources = expenseSources(c);
    const categoryId = drillCategoryIdForExpenseSlice(sources);
    const color =
      sources.find((s) => s.id === categoryId)?.color ||
      c.color ||
      "#94a3b8";
    const rowKey =
      sources.length > 1
        ? sources.map((s) => s.id).sort().join("|")
        : categoryId;
    return {
      name: c.name,
      color,
      value: sliceValue,
      categoryId,
      rowKey,
    };
  }

  return cats
    .map((c) =>
      mapCategory(c, Object.values(c.totals).reduce((a, b) => a + b, 0)),
    )
    .filter((r) => r.value > 0);
}

type Props = {
  summary: CashflowSummary | undefined;
  currency: Currency;
  isLoading: boolean;
  /** When true, fetch rolling 30-day totals via consolidated cashflow (must match dashboard toggle). */
  useConsolidatedSummary: boolean;
};

type ExpenseTooltipPayload = {
  name: string;
  value: number;
  color: string;
};

function ExpenseBreakdownSkeleton() {
  const legendDots = [
    "bg-emerald-400/55",
    "bg-amber-400/55",
    "bg-orange-400/55",
    "bg-rose-400/55",
  ];

  return (
    <div className="rounded-xl bg-[#f5f6f8] p-3">
      <div className="flex min-h-[200px] flex-col gap-3 lg:flex-row lg:items-start">
        <div className="relative mx-auto h-[128px] w-[128px] shrink-0 lg:mx-0">
          <div
            className="h-full w-full rounded-full"
            style={{
              background:
                "conic-gradient(#7ec8a2 0deg 162deg, #ebc980 162deg 259.2deg, #e9bb8e 259.2deg 309.6deg, #e7a2a7 309.6deg 360deg)",
            }}
            aria-hidden
          />
          <div
            className="absolute inset-[24px] rounded-full bg-[#f5f6f8]"
            aria-hidden
          />
        </div>

        <div className="min-w-0 flex-1 space-y-2 lg:pt-1">
          {legendDots.map((dotClass, index) => (
            <div
              key={dotClass}
              className="flex items-center justify-between gap-6"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={`h-2.5 w-2.5 rounded-full ${dotClass}`}
                  aria-hidden
                />
                <span
                  className="block h-3.5 rounded-full bg-slate-500/25"
                  style={{ width: `${index % 2 === 0 ? 110 : 92}px` }}
                  aria-hidden
                />
              </div>
              <span
                className="h-3.5 w-12 rounded-full bg-slate-500/25"
                aria-hidden
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function DashboardExpenseBreakdown({
  summary,
  currency,
  isLoading,
  useConsolidatedSummary,
}: Props) {
  const [range, setRange] = useState<RangeMode>("rolling30");
  const { categories } = useCategories();

  const rolling30Key =
    range === "rolling30"
      ? useConsolidatedSummary
        ? cfApi(
            `/cashflow/consolidated?${new URLSearchParams({
              displayCurrency: currency,
              ...last30DaysRange(),
            }).toString()}`,
          )
        : cfApi(
            `/cashflow?${new URLSearchParams({
              currency,
              ...last30DaysRange(),
            }).toString()}`,
          )
      : null;

  const { data: rollingSummary, isLoading: rollingLoading } =
    useSWR<CashflowSummary>(
      rolling30Key,
      rolling30Key ? fetchCashflowSummaryForBreakdown : null,
    );

  const effectiveSummary = range === "rolling30" ? rollingSummary : summary;

  const expenseCategoriesRollup = useMemo(() => {
    if (!effectiveSummary?.expenseCategories?.length) return [];
    const base =
      categories.length > 0
        ? rollupExpenseCategoriesToParentsOnly(
            effectiveSummary.expenseCategories,
            categories,
          )
        : effectiveSummary.expenseCategories;
    const merged = mergeExpenseCategoriesByNormalizedName(base);
    return merged;
  }, [effectiveSummary?.expenseCategories, categories]);

  const breakdownLoading =
    range === "rolling30" ? rollingLoading : isLoading;

  const rows = useMemo(() => {
    if (breakdownLoading || !effectiveSummary) return [];
    const built = buildExpenseRows({
      ...effectiveSummary,
      expenseCategories: expenseCategoriesRollup,
    });
    return built;
  }, [
    breakdownLoading,
    effectiveSummary,
    expenseCategoriesRollup,
  ]);

  if (breakdownLoading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 h-9 animate-pulse rounded bg-slate-100" />
        <ExpenseBreakdownSkeleton />
      </div>
    );
  }

  const total = rows.reduce((s, r) => s + r.value, 0);
  const renderPieTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: ReadonlyArray<{
      payload: ExpenseTooltipPayload;
      value: number;
      name: string;
    }>;
  }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0];
    const amount = Number(point.value) || 0;
    return (
      <div className="rounded-md border border-slate-200 bg-white/95 px-2.5 py-1.5 text-xs shadow-lg backdrop-blur-sm">
        <p className="font-medium text-slate-900">{point.name}</p>
        <p className="tabular-nums text-slate-700">
          {formatCurrency(amount, currency)}
        </p>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex min-h-[56px] flex-col justify-between gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-slate-900">
            Cash Out Breakdown
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-500">Dates:</span>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as RangeMode)}
            className="rounded-lg bg-slate-50 px-3 py-1.5 text-slate-800"
            aria-label="Expense date range"
          >
            <option value="rolling30">Last 30 days</option>
            <option value="full">Full range</option>
          </select>
        </div>
      </div>

      {rows.length === 0 || total <= 0 ? (
        <PieChartEmptyContent
          ctaTo="/files-integrations"
          ctaLabel="Upload transactions"
        />
      ) : (
        <div className="flex min-h-[240px] flex-col gap-4 lg:flex-row lg:items-start">
          <div className="relative mx-auto w-[165px] shrink-0 lg:mx-0">
            <ResponsiveContainer width={165} height={165}>
              <PieChart>
                <Pie
                  data={rows}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={52}
                  outerRadius={78}
                  paddingAngle={0}
                  stroke="#fff"
                  strokeWidth={1}
                >
                  {rows.map((entry) => (
                    <Cell key={entry.rowKey} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={renderPieTooltip}
                  allowEscapeViewBox={{ x: true, y: true }}
                  wrapperStyle={{ zIndex: 9999, pointerEvents: "none" }}
                  cursor={{ fill: "rgba(15, 23, 42, 0.08)" }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pt-1 text-center">
              <p className="text-xs font-medium text-slate-500">Total</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900">
                {formatCenterTotal(total, currency)}
              </p>
            </div>
          </div>
          <div className="min-w-0 flex-1 space-y-2 overflow-y-auto pr-1 lg:max-h-[220px]">
            {rows
              .slice()
              .sort((a, b) => b.value - a.value)
              .map((r) => {
                const pct = total > 0 ? (r.value / total) * 100 : 0;
                return (
                  <div
                    key={r.rowKey}
                    className="box-border flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: r.color }}
                      />
                      <span className="truncate text-slate-800">
                        {r.name}{" "}
                        <span className="text-slate-400">
                          {pct.toFixed(1)}%
                        </span>
                      </span>
                    </div>
                    <span className="shrink-0 tabular-nums font-medium text-slate-700">
                      {formatCurrency(r.value, currency)}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
